import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import cn from 'classnames';
import { useDevice } from '../../providers/Device';
import { useIntentBehavior } from '../../providers/IntentBehavior';
import { useBiAction } from '../BiTracking';
import { useIsInCallout } from '../Callout';
import { CircularLoader } from '../CircularLoader';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Tooltip } from '../Tooltip';
import classes from './Button.module.scss';
import { downsizeForCallout, spinnerSize } from './Button.utils';
export function Button(props) {
    var _ref, _props_roundness;
    const { className, type, size = 'medium', mobile, skin = 'primary', prefixIcon, suffixIcon, as, ref, children, fluid, intent, disabled, loading, tooltip, noWrap, // SplitButton introduces these props, which causes React console errors about unknown props for button element, so we ignore them here.
    disableElevation: _disableElevation, disableFocusRipple: _disableFocusRipple, disableRipple: _disableRipple, fullWidth: _fullWidth, bi, noBi: _noBi, onClick, testId, disabledSkin } = props, rest = _object_without_properties(props, [
        "className",
        "type",
        "size",
        "mobile",
        "skin",
        "prefixIcon",
        "suffixIcon",
        "as",
        "ref",
        "children",
        "fluid",
        "intent",
        "disabled",
        "loading",
        "tooltip",
        "noWrap",
        "disableElevation",
        "disableFocusRipple",
        "disableRipple",
        "fullWidth",
        "bi",
        "noBi",
        "onClick",
        "testId",
        "disabledSkin"
    ]);
    const { isMobileDevice } = useDevice();
    const isInCallout = useIsInCallout();
    const collapsesToIcon = isMobileDevice && (mobile === null || mobile === void 0 ? void 0 : mobile.icon) != null;
    const mobileSize = collapsesToIcon ? 'iconOnly' : (_ref = mobile === null || mobile === void 0 ? void 0 : mobile.size) !== null && _ref !== void 0 ? _ref : size;
    const deviceSize = isMobileDevice ? mobileSize : size;
    const effectiveSize = isInCallout && isMobileDevice ? downsizeForCallout(deviceSize) : deviceSize;
    const roundness = (_props_roundness = props.roundness) !== null && _props_roundness !== void 0 ? _props_roundness : 'm';
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const isDisabled = disabledFromIntent || disabled || loading;
    const isFocusedDisabled = isDisabled && disabledSkin === 'focused';
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const hasTooltip = !!(tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) || !!tooltipFromIntent;
    const Component = as || 'button';
    const logBiAction = useBiAction();
    const handleClick = useCallback((event)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'button',
                entityName: bi.name,
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        return onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [
        bi,
        logBiAction,
        onClick,
        testId
    ]);
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: hasTooltip,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({}, tooltip || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn(classes.wrapper, {
                        [classes.fluid]: fluid
                    }, className),
                    "data-testid": testId && "".concat(testId, "-wrapper"),
                    children: children
                })
            })),
        children: /*#__PURE__*/ _jsxs(Component, _object_spread_props(_object_spread({
            ref: ref,
            type: type || 'button',
            disabled: isDisabled,
            "aria-busy": loading !== null && loading !== void 0 ? loading : false,
            className: cn(classes.root, classes["".concat(effectiveSize, "Size")], classes["".concat(skin, "Skin")], classes["".concat(roundness, "Roundness")], {
                [classes.fluid]: fluid,
                [classes.noWrap]: noWrap,
                [classes.focusedDisabled]: isFocusedDisabled,
                [classes.loading]: loading
            }, !hasTooltip && className),
            onClick: handleClick
        }, rest, testId != null ? {
            'data-testid': testId
        } : {}), {
            children: [
                collapsesToIcon ? mobile.icon : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        prefixIcon && /*#__PURE__*/ _jsx("div", {
                            className: classes.prefixIcon,
                            children: prefixIcon
                        }),
                        children && /*#__PURE__*/ _jsx("span", {
                            className: classes.content,
                            children: children
                        }),
                        suffixIcon && /*#__PURE__*/ _jsx("div", {
                            className: classes.suffixIcon,
                            children: suffixIcon
                        })
                    ]
                }),
                loading && /*#__PURE__*/ _jsx("div", {
                    className: classes.loader,
                    role: "status",
                    "aria-label": "Loading",
                    "data-testid": testId && "".concat(testId, "-loading"),
                    children: /*#__PURE__*/ _jsx(CircularLoader, {
                        size: spinnerSize[effectiveSize],
                        color: "inverted"
                    })
                })
            ]
        }))
    });
}
